import React, { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useCall } from "context/CallContext"
import { getVisitCallMode } from "helpers/utils"
import MicOffIcon from '@mui/icons-material/MicOff'
import MicIcon from '@mui/icons-material/Mic'
import CallEndIcon from '@mui/icons-material/CallEnd'
import VideocamIcon from '@mui/icons-material/Videocam'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import FullscreenIcon from '@mui/icons-material/Fullscreen'

const VideoChat = ({
  roomName: propRoomName = "",
  visitType: propVisitType = "video",
  userName: propUserName = "",
  overlay = false,
}) => {
  const { videoId } = useParams()

  const {
    channelName,
    setChannelName,
    joined,
    connecting,
    isMuted,
    isVideoOff,
    remoteUsers,
    remoteVideoOff,
    remoteMicMuted,
    showRemoteLeft,
    callStarted,
    callDuration,
    callViewMode,
    isVideoCall,
    hasActiveCallView,
    setHasActiveCallView,
    joinCall,
    leaveCall,
    toggleMic,
    toggleVideo,
    setCallViewMode,
    waitAndReplay,
  } = useCall()

  const roomName = channelName || videoId || propRoomName || ""
const visitType = propVisitType || (isVideoCall ? "video" : "audio")  // ← keep this here
const isAudioVisit = propVisitType?.trim().toLowerCase().includes("audio")  // ← add this here too
  // ── Sync channel name ──────────────────────────────────────────────────────
  useEffect(() => {
    const targetChannel = videoId || propRoomName
    if (targetChannel && channelName !== targetChannel) {
      setChannelName(targetChannel)
    }
  }, [videoId, propRoomName, channelName, setChannelName])

  // ── Register this view as active ───────────────────────────────────────────
  useEffect(() => {
    setHasActiveCallView(true)
    return () => setHasActiveCallView(false)
  }, [setHasActiveCallView])

  // ── FIX: replay tracks whenever view mode changes, using context function ──
  // Removed local replayLocalVideo entirely — CallContext.waitAndReplay handles it.
  useEffect(() => {
    if (!joined || !isVideoCall) return

    const timeout = window.setTimeout(() => {
      waitAndReplay()
    }, 240)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [joined, callViewMode, waitAndReplay, isVideoCall])

  // ── Display name ───────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState("")
  const auth = JSON.parse(localStorage.getItem("authUser") || "{}")
  const defaultName = auth?.fullName || auth?.name || auth?.firstName || "Veterinarian"
  const effectiveName = propUserName || displayName.trim() || defaultName

  // ── Fullscreen toggle ──────────────────────────────────────────────────────
  const handleFullscreen = useCallback(() => {
    setCallViewMode("fullscreen")
    window.setTimeout(() => {
      waitAndReplay()
    }, 220)
  }, [setCallViewMode, waitAndReplay])

  const handleExitFullscreen = useCallback(() => {
    setCallViewMode("original")
    window.setTimeout(() => {
      waitAndReplay()
    }, 220)
  }, [setCallViewMode, waitAndReplay])

  // ── FIX: container IDs — no longer gated on `overlay`, just on callViewMode ─
  const isFullscreenView = callViewMode === "fullscreen"

  const localVideoContainerId = isFullscreenView
    ? "fullscreen-local-video"
    : "local-video"

  const getRemoteVideoId = (user) => {
    if (isFullscreenView) return `fullscreen-remote-video-${user.uid}`
    return `remote-video-${user.uid}`
  }

  const showFullscreenButton = !overlay

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleJoin = async () => {
    if (!roomName) return
    const callMode = getVisitCallMode(visitType)
    await joinCall({ channel: roomName, visitType: callMode, user: effectiveName })
  }

  const formatCallDuration = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0")
    const secs = String(seconds % 60).padStart(2, "0")
    return `${mins}:${secs}`
  }

  // ── Not yet joined — show lobby ────────────────────────────────────────────
  if (!joined) {
  const isAudioVisit = propVisitType?.trim().toLowerCase().includes("audio")
  return (
    <div style={styles.lobby}>
      <div style={styles.lobbyCard}>
        <div style={styles.lobbyIconWrap}>
          {!isAudioVisit
            ? <VideocamIcon style={{ fontSize: 36, color: "#22c55e" }} />
            : <PhoneIcon style={{ fontSize: 36, color: "#22c55e" }} />}
        </div>
        <h2 style={styles.lobbyTitle}>
          {!isAudioVisit ? "Join Video Call" : "Join Audio Call"}
        </h2>
        <p style={styles.lobbyRoom}>Room: <strong>{roomName || "Unknown"}</strong></p>
        <p style={styles.lobbyUser}>Joining as: <strong>{effectiveName}</strong></p>
        <button
          onClick={handleJoin}
          disabled={connecting || joined || !roomName}
          style={{
            ...styles.joinBtn,
            opacity: connecting ? 0.7 : 1,
            cursor: connecting ? "not-allowed" : "pointer",
          }}
        >
          {connecting ? "Connecting..." : !isAudioVisit ? "Join Video Call" : "Join Audio Call"}
        </button>
      </div>
    </div>
  )
}

  // ── FIX: only hide when MINIMIZED (not fullscreen) and not overlay ─────────
  if (callViewMode === "minimized" && !overlay) {
    return null
  }

  // ── Audio call UI ──────────────────────────────────────────────────────────
  if (!isVideoCall) {
    return (
      <div style={styles.audioCallContainer}>
        <div style={styles.audioContent}>
          {callStarted && (
            <div style={styles.audioTimer}>{formatCallDuration(callDuration)}</div>
          )}
          <div style={styles.audioAvatar}>
            <PersonIcon style={{ fontSize: 56, color: "#fff" }} />
          </div>
          <p style={styles.audioStatus}>
            {remoteUsers.length > 0 ? "Connected" : "Waiting for other participant..."}
          </p>
          <p style={styles.audioRoomLabel}>{channelName}</p>
          {remoteUsers.length > 0 && remoteMicMuted && (
            <div style={styles.audioBanner}>
              <MicOffIcon style={{ fontSize: 16, marginRight: 6 }} />Guest muted
            </div>
          )}
          {showRemoteLeft && <div style={styles.audioBanner}>Guest left the call</div>}
        </div>

        <div style={styles.controls}>
          <button
            onClick={toggleMic}
            style={{ ...styles.controlBtn, backgroundColor: isMuted ? "#e53935" : "rgba(255,255,255,0.15)" }}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </button>
          <button onClick={leaveCall} style={styles.endBtn} title="End call">
            <CallEndIcon />
          </button>
          <button
            onClick={() => setCallViewMode("minimized")}
            style={{ ...styles.controlBtn, backgroundColor: "rgba(255,255,255,0.15)" }}
            title="Minimize call"
          >
            <CloseFullscreenIcon />
          </button>
        </div>
      </div>
    )
  }

  // ── Video call UI ──────────────────────────────────────────────────────────
  return (
    <div style={isFullscreenView ? styles.fullscreenCallContainer : styles.callContainer}>
      {callStarted && (
        <div style={styles.callTimer}>{formatCallDuration(callDuration)}</div>
      )}

      {/* FIX: exit fullscreen button visible when in fullscreen mode */}
      {isFullscreenView && (
        <button
          onClick={handleExitFullscreen}
          style={styles.exitFullscreenBtn}
          title="Exit fullscreen"
        >
          <CloseFullscreenIcon style={{ fontSize: 20 }} />
        </button>
      )}

      {remoteUsers.length > 0 ? (
        remoteUsers.map(user => (
          <div
            key={user.uid}
            id={getRemoteVideoId(user)}
            style={styles.remoteVideo}
          />
        ))
      ) : (
        <div style={styles.waitingContainer}>
          <div style={styles.waitingContent}>
            <div style={styles.waitingIcon}>
              <HourglassBottomIcon style={{ fontSize: 48, color: "#fff" }} />
            </div>
            <p style={styles.waitingText}>Waiting for other participant...</p>
            <p style={styles.waitingSubText}>Room: {channelName}</p>
          </div>
        </div>
      )}

      {!isVideoOff ? (
        <div
          id={localVideoContainerId}
          style={{ ...styles.localVideo, minWidth: 120, minHeight: 90 }}
        />
      ) : (
        <div style={{ ...styles.localVideo, ...styles.localVideoOff }}>
          Camera Off
        </div>
      )}

      {(isMuted || isVideoOff) && (
        <div style={styles.localOverlay}>
          {isMuted && <MicOffIcon style={{ fontSize: 14 }} />}
          {isVideoOff && <VideocamOffIcon style={{ fontSize: 14 }} />}
        </div>
      )}

      {remoteUsers.length > 0 && remoteVideoOff && (
        <div style={styles.banner}>Guest camera is off</div>
      )}
      {remoteUsers.length > 0 && remoteMicMuted && (
        <div style={styles.bannerTop}>
          <MicOffIcon style={{ fontSize: 16, marginRight: 6 }} />Guest muted
        </div>
      )}
      {showRemoteLeft && <div style={styles.banner}>Guest left the call</div>}

      <div style={styles.controls}>
        <button
          onClick={toggleMic}
          style={{ ...styles.controlBtn, backgroundColor: isMuted ? "#e53935" : "rgba(255,255,255,0.15)" }}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? <MicOffIcon /> : <MicIcon />}
        </button>

        <button onClick={leaveCall} style={styles.endBtn} title="End call">
          <CallEndIcon />
        </button>

        <button
          onClick={toggleVideo}
          style={{ ...styles.controlBtn, backgroundColor: isVideoOff ? "#e53935" : "rgba(255,255,255,0.15)" }}
          title={isVideoOff ? "Turn on camera" : "Turn off camera"}
        >
          {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
        </button>

        {showFullscreenButton && !isFullscreenView && (
          <button
            onClick={handleFullscreen}
            style={{ ...styles.controlBtn, backgroundColor: "rgba(255,255,255,0.15)" }}
            title="Fullscreen"
          >
            <FullscreenIcon />
          </button>
        )}

        {!isFullscreenView && (
          <button
            onClick={() => setCallViewMode("minimized")}
            style={{ ...styles.controlBtn, backgroundColor: "rgba(255,255,255,0.15)" }}
            title="Minimize call"
          >
            <CloseFullscreenIcon />
          </button>
        )}
      </div>
    </div>
  )
}

const styles = {
  lobby: {
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "100%", minHeight: 400, backgroundColor: "#0f0f0f",
  },
  lobbyCard: {
    backgroundColor: "#1a1a1a", borderRadius: 16, padding: "48px 40px",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
    minWidth: 280, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  lobbyIconWrap: {
    width: 72, height: 72, borderRadius: "50%",
    backgroundColor: "rgba(34,197,94,0.12)",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8,
  },
  lobbyTitle: { color: "#ffffff", fontSize: 22, fontWeight: 600, margin: 0 },
  lobbyRoom: { color: "#aaaaaa", fontSize: 14, margin: 0 },
  lobbyUser: { color: "#aaaaaa", fontSize: 14, margin: 0 },
  joinBtn: {
    marginTop: 16, padding: "14px 40px", fontSize: 16, fontWeight: 600,
    borderRadius: 50, backgroundColor: "#22c55e", color: "#fff",
    border: "none", outline: "none", transition: "transform 0.1s",
  },
  audioCallContainer: {
    position: "relative", width: "100%", height: "100%", minHeight: 400,
    backgroundColor: "#1a1a2e", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
  },
  audioContent: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 12, flex: 1, justifyContent: "center",
  },
  audioAvatar: {
    width: 100, height: 100, borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.12)",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8,
  },
  audioStatus: { color: "#ffffff", fontSize: 18, fontWeight: 500, margin: 0 },
  audioRoomLabel: { color: "#888", fontSize: 13, margin: 0 },
  audioBanner: {
    marginTop: 12, display: "flex", alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)", color: "#fff",
    padding: "8px 16px", borderRadius: 8, fontSize: 14,
  },
  audioTimer: {
    backgroundColor: "rgba(255,255,255,0.12)", color: "#fff",
    padding: "8px 14px", borderRadius: 8, fontSize: 14, fontWeight: 600, marginBottom: 12,
  },
  // In styles object, update callContainer:
  callContainer: {
  position: "relative",
  width: "100%",
  height: "100%",
  minHeight: 500,          // ← increase from 420
  backgroundColor: "#000",
  overflow: "hidden",
  display: "flex",         // ← add
  flexDirection: "column", // ← add
    },
  
  // FIX: fullscreen variant covers the entire viewport with explicit dimensions
  fullscreenCallContainer: {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    minWidth: "100vw",
    minHeight: "100vh",
    zIndex: 1000000,
    backgroundColor: "#000",
    overflow: "hidden",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "stretch",
  },
  exitFullscreenBtn: {
    position: "absolute", top: 16, right: 16, zIndex: 1000001,
    width: 40, height: 40, borderRadius: "50%", border: "none",
    backgroundColor: "#111111a8", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer",
  },
  remoteVideo: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "#111",
    objectFit: "cover",
    zIndex: 1,
  },
  waitingContainer: {
    width: "100%", height: "100%", display: "flex",
    alignItems: "center", justifyContent: "center", backgroundColor: "#111",
  },
  waitingContent: { textAlign: "center" },
  waitingIcon: { marginBottom: 16 },
  waitingText: { color: "#ffffff", fontSize: 18, fontWeight: 500, margin: "0 0 8px" },
  waitingSubText: { color: "#888888", fontSize: 13, margin: 0 
  },
  localVideo: {
  position: "absolute",
  bottom: 100,
  right: 16,
  width: 180,
  height: 220,
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: "#1a1a1a",
  zIndex: 100,
  minWidth: 180,
  minHeight: 220,
  boxShadow: "0 12px 30px rgba(0,0,0,0.4)",
},
  localVideoOff: {
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#888", fontSize: 13,
  },
  localOverlay: {
    position: "absolute", bottom: 106, right: 20,
    backgroundColor: "rgba(0,0,0,0.65)", borderRadius: 6,
    padding: "3px 6px", display: "flex", gap: 4, color: "#fff", zIndex: 101,
  },
  banner: {
    position: "absolute", bottom: 108, left: "50%", transform: "translateX(-50%)",
    backgroundColor: "rgba(0,0,0,0.7)", padding: "8px 16px",
    borderRadius: 8, color: "#fff", fontSize: 14, zIndex: 50, whiteSpace: "nowrap",
  },
  bannerTop: {
    position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)",
    backgroundColor: "rgba(0,0,0,0.7)", padding: "8px 16px",
    borderRadius: 8, color: "#fff", fontSize: 14,
    display: "flex", alignItems: "center", zIndex: 50, whiteSpace: "nowrap",
  },
  callTimer: {
    position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
    backgroundColor: "rgba(0,0,0,0.7)", color: "#fff",
    padding: "8px 16px", borderRadius: 20, fontSize: 14, fontWeight: 600, zIndex: 999,
  },
  controls: {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: 90,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: 16,
  padding: "0 16px",
  backgroundColor: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(8px)",
  zIndex: 20,
},
  controlBtn: {
    width: 52, height: 52, borderRadius: "50%", border: "none", fontSize: 20,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background-color 0.2s, transform 0.1s", color: "#fff",
    backgroundColor: "rgba(255,255,255,0.15)",
  },
  endBtn: {
    width: 52, height: 52, borderRadius: "50%", border: "none", fontSize: 24,
    cursor: "pointer", backgroundColor: "#e53935",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
  },
}

export default VideoChat
